import apiClient from '@sdk/client';
import { Button } from '@src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@src/components/ui/dialog';
import { useIndexStatus, type IndexStatusPerType } from '@src/hooks/use-index-status';
import { applyScopeToParams, type ScopeFilter } from '@src/lib/scope-filter';
import { Ghost, Loader2, RefreshCw, Trash2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

const BASE = '/graph/compute_node/@local/fs-records';

export type SweepAction = 'ignore' | 'delete';

interface SweepOrphansDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set, scopes the dialog to a single type. When omitted, lists every type with orphans. */
  scopeType?: string | null;
  /** Pre-fetched per-type rows from the page. The dialog stays in sync via useIndexStatus.refresh() after each action. */
  perType: IndexStatusPerType[];
  /** Total across all types — drives the toolbar badge upstream; passed here for the summary line. */
  totalOrphans: number;
  /** Optional ScopeFilter — when set, sweep and re-scan calls narrow to that
   *  subset (and the index-status refresh after each action follows the same
   *  scope so the orphan counts re-fetched match what the page sees). */
  scope?: ScopeFilter;
}

function appendScope(url: string, scope?: ScopeFilter): string {
  if (!scope) return url;
  const p = new URLSearchParams();
  applyScopeToParams(p, scope);
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}${p.toString()}`;
}

export function SweepOrphansDialog({
  open,
  onOpenChange,
  scopeType,
  perType,
  totalOrphans,
  scope,
}: SweepOrphansDialogProps) {
  const [action, setAction] = useState<SweepAction>('ignore');
  const [scanning, setScanning] = useState(false);
  const [sweeping, setSweeping] = useState(false);
  const { refresh: refreshIndexStatus } = useIndexStatus(scope);
  const { t } = useLingui();

  const orphanRows = useMemo(() => {
    const rows = perType.filter((p) => p.orphan_count > 0);
    if (scopeType) return rows.filter((p) => p.type_name === scopeType);
    return rows.sort((a, b) => b.orphan_count - a.orphan_count);
  }, [perType, scopeType]);

  const scopeTotal = useMemo(() => orphanRows.reduce((s, r) => s + r.orphan_count, 0), [orphanRows]);

  // Re-scan: a no-op-effect index call that re-walks roots, updates the
  // orphan flags on DB rows, and emits progress so the footer pill +
  // page progress bar update. Hooks into the same WS stream the rest of
  // the page uses.
  const handleRescan = useCallback(async () => {
    setScanning(true);
    try {
      await apiClient.post(appendScope(`${BASE}/index?orphan_action=index`, scope));
    } finally {
      setScanning(false);
      refreshIndexStatus();
    }
  }, [refreshIndexStatus, scope]);

  // Sweep: POST /fs-records/index?orphan_action=ignore|delete, optionally
  // scoped to one type. Same backend path the indexer uses internally; WS
  // progress fires automatically.
  const handleSweep = useCallback(async () => {
    setSweeping(true);
    try {
      const baseUrl = scopeType
        ? `${BASE}/index?type=${encodeURIComponent(scopeType)}&orphan_action=${action}`
        : `${BASE}/index?orphan_action=${action}`;
      await apiClient.post(appendScope(baseUrl, scope));
    } finally {
      setSweeping(false);
      refreshIndexStatus();
      onOpenChange(false);
    }
  }, [scopeType, action, refreshIndexStatus, onOpenChange, scope]);

  const busy = scanning || sweeping;
  const actionWord = action === 'delete' ? t`delete` : t`ignore`;
  const title = scopeType ? t`Orphan ${scopeType} records` : t`Orphan records`;
  const headerCount = scopeType ? scopeTotal : totalOrphans;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Ghost className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            {title}
            {headerCount > 0 && (
              <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-300">
                {headerCount}
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-xs leading-relaxed">
            <Trans>
              An <span className="font-medium">orphan</span> is a row in the search index whose source file is gone from
              disk. The indexer detects them on every run and marks them with{' '}
              <code className="font-mono">orphan_since</code>. The file is already gone; sweeping just removes the stale
              row from search so results stay clean.
            </Trans>
          </DialogDescription>
        </DialogHeader>

        {orphanRows.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-8 text-center text-sm text-muted-foreground">
            <Ghost className="h-8 w-8 opacity-30" />
            <p className="font-medium text-foreground">
              <Trans>No orphans found.</Trans>
            </p>
            <p className="text-xs">
              {scopeType ? (
                <Trans>Every {scopeType} row has a source file on disk.</Trans>
              ) : (
                <Trans>Every indexed row has a source file on disk.</Trans>
              )}
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2 h-7 gap-1.5 text-xs"
              onClick={() => void handleRescan()}
              disabled={busy}
            >
              <RefreshCw className={`h-3.5 w-3.5 ${scanning ? 'animate-spin' : ''}`} />
              {scanning ? t`Rescanning…` : t`Re-scan now`}
            </Button>
          </div>
        ) : (
          <>
            <div className="max-h-[40vh] overflow-y-auto rounded border bg-card">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/50 text-muted-foreground">
                    <th className="py-1.5 pe-4 ps-3 text-start font-medium">
                      <Trans>Type</Trans>
                    </th>
                    <th className="py-1.5 pe-3 text-end font-medium">
                      <Trans>Orphans</Trans>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {orphanRows.map((r) => (
                    <tr key={r.type_name} className="border-b last:border-0">
                      <td className="py-1.5 pe-4 ps-3 font-mono">{r.type_name}</td>
                      <td className="py-1.5 pe-3 text-end tabular-nums text-amber-600 dark:text-amber-400">
                        {r.orphan_count}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <fieldset className="flex flex-col gap-2 text-xs">
              <legend className="font-medium">
                <Trans>Sweep action</Trans>
              </legend>
              <label className="flex cursor-pointer items-start gap-2">
                <input
                  type="radio"
                  name="sweep-action"
                  className="mt-0.5"
                  checked={action === 'ignore'}
                  onChange={() => setAction('ignore')}
                  disabled={busy}
                />
                <span>
                  <Trans>
                    <span className="font-medium">Ignore</span> — remove the DB row, keep the shadow record dir at{' '}
                    <code className="font-mono">~/.flow/records/&lt;type&gt;/&lt;id&gt;/</code> as a forensic
                    breadcrumb. <span className="text-muted-foreground">(recommended)</span>
                  </Trans>
                </span>
              </label>
              <label className="flex cursor-pointer items-start gap-2">
                <input
                  type="radio"
                  name="sweep-action"
                  className="mt-0.5"
                  checked={action === 'delete'}
                  onChange={() => setAction('delete')}
                  disabled={busy}
                />
                <span>
                  <Trans>
                    <span className="font-medium">Delete</span> — remove the DB row <em>and</em> the shadow record dir.
                    Reclaims disk space; no recovery path.
                  </Trans>
                </span>
              </label>
            </fieldset>
          </>
        )}

        <DialogFooter className="gap-2">
          {orphanRows.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1.5 text-xs"
              onClick={() => void handleRescan()}
              disabled={busy}
            >
              <RefreshCw className={`h-3.5 w-3.5 ${scanning ? 'animate-spin' : ''}`} />
              <Trans>Re-scan</Trans>
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            <Trans>Close</Trans>
          </Button>
          {orphanRows.length > 0 && (
            <Button
              variant="default"
              size="sm"
              className="h-8 gap-1.5 bg-amber-600 text-xs text-white hover:bg-amber-700"
              onClick={() => void handleSweep()}
              disabled={busy}
            >
              {sweeping ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              {sweeping ? t`Sweeping…` : t`Sweep ${scopeTotal} (${actionWord})`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
